/**
 * Handlers for absolutely positioned elements
 */
import { v4 as uuidv4 } from 'uuid'
import type { AbsoluteElement } from '../types/template.types'

export interface AbsoluteElementHandlers {
  handleAddAbsoluteImage: () => void
  handleCutAbsoluteElement: (elementId: string) => void
  handleCopyAbsoluteElement: (elementId: string) => void
  handlePasteAbsoluteElement: () => void
  handleDuplicateAbsoluteElement: (elementId: string) => void
  handleBringToFront: (elementId: string) => void
  handleSendToBack: (elementId: string) => void
  handleBringForward: (elementId: string) => void
  handleSendBackward: (elementId: string) => void
}

export const createAbsoluteElementHandlers = (
  absoluteElements: AbsoluteElement[],
  setAbsoluteElements: (elements: AbsoluteElement[]) => void,
  clipboard: AbsoluteElement | null,
  setClipboard: (element: AbsoluteElement | null) => void,
  setSelectedElementId: (id: string | null) => void
): AbsoluteElementHandlers => {
  const handleAddAbsoluteImage = () => {
    // Calculate next z-index (highest current + 1, or start at 101 to be above grid)
    // Grid is at z-index 100, so elements start at 101 (above grid)
    const maxZIndex = absoluteElements.length > 0 
      ? Math.max(...absoluteElements.map(el => el.zIndex || 101))
      : 100

    const newElement: AbsoluteElement = {
      id: uuidv4(),
      type: 'absolute-image',
      visible: true,
      x: 100, // Default position
      y: 100,
      width: 200,
      height: 100,
      aspectRatioLocked: true,
      zIndex: maxZIndex + 1,
    }

    setAbsoluteElements([...absoluteElements, newElement])
    setSelectedElementId(newElement.id)
  }

  const handleCutAbsoluteElement = (elementId: string) => {
    const element = absoluteElements.find(el => el.id === elementId)
    if (element) {
      setClipboard(element)
      setAbsoluteElements(absoluteElements.filter(el => el.id !== elementId))
      setSelectedElementId(null)
    }
  }

  const handleCopyAbsoluteElement = (elementId: string) => {
    const element = absoluteElements.find(el => el.id === elementId)
    if (element) {
      setClipboard(element)
    }
  }

  const handlePasteAbsoluteElement = () => {
    if (!clipboard) return

    const maxZIndex = absoluteElements.length > 0 
      ? Math.max(...absoluteElements.map(el => el.zIndex || 500))
      : 500

    const newElement: AbsoluteElement = {
      ...clipboard,
      id: uuidv4(),
      x: clipboard.x + 20,
      y: clipboard.y + 20,
      zIndex: maxZIndex + 1,
    }

    setAbsoluteElements([...absoluteElements, newElement])
    setSelectedElementId(newElement.id)
  }

  const handleDuplicateAbsoluteElement = (elementId: string) => {
    const element = absoluteElements.find(el => el.id === elementId)
    if (!element) return

    const maxZIndex = absoluteElements.length > 0 
      ? Math.max(...absoluteElements.map(el => el.zIndex || 500))
      : 500

    const newElement: AbsoluteElement = {
      ...element,
      id: uuidv4(),
      x: element.x + 20,
      y: element.y + 20,
      zIndex: maxZIndex + 1,
    }

    setAbsoluteElements([...absoluteElements, newElement])
    setSelectedElementId(newElement.id)
  }

  const handleBringToFront = (elementId: string) => {
    const maxZIndex = absoluteElements.length > 0
      ? Math.max(...absoluteElements.map(el => el.zIndex || 500))
      : 500
    setAbsoluteElements(absoluteElements.map(el => 
      el.id === elementId ? { ...el, zIndex: Math.max(maxZIndex + 1, 501) } : el
    ))
  }

  const handleSendToBack = (elementId: string) => {
    const minZIndex = absoluteElements.length > 0
      ? Math.min(...absoluteElements.map(el => el.zIndex || 500))
      : 500
    // Allow going down to 1 to go behind header/root (both at 500)
    setAbsoluteElements(absoluteElements.map(el => 
      el.id === elementId ? { ...el, zIndex: Math.max(1, minZIndex - 1) } : el
    ))
  }

  const handleBringForward = (elementId: string) => {
    const element = absoluteElements.find(el => el.id === elementId)
    if (!element) return

    const currentZ = element.zIndex || 500
    const higherElements = absoluteElements.filter(el => (el.zIndex || 500) > currentZ)

    if (higherElements.length > 0) {
      const nextZ = Math.min(...higherElements.map(el => el.zIndex || 500))
      setAbsoluteElements(absoluteElements.map(el => {
        if (el.id === elementId) return { ...el, zIndex: nextZ + 1 }
        if (el.zIndex === nextZ) return { ...el, zIndex: Math.max(1, nextZ - 1) }
        return el
      }))
    }
  }

  const handleSendBackward = (elementId: string) => {
    const element = absoluteElements.find(el => el.id === elementId)
    if (!element) return

    const currentZ = element.zIndex || 500
    const lowerElements = absoluteElements.filter(el => (el.zIndex || 500) < currentZ)

    if (lowerElements.length > 0) {
      const nextZ = Math.max(...lowerElements.map(el => el.zIndex || 500))
      setAbsoluteElements(absoluteElements.map(el => {
        if (el.id === elementId) return { ...el, zIndex: Math.max(1, nextZ - 1) }
        if (el.zIndex === nextZ) return { ...el, zIndex: nextZ + 1 }
        return el
      }))
    }
  }

  return {
    handleAddAbsoluteImage,
    handleCutAbsoluteElement,
    handleCopyAbsoluteElement,
    handlePasteAbsoluteElement,
    handleDuplicateAbsoluteElement,
    handleBringToFront,
    handleSendToBack,
    handleBringForward,
    handleSendBackward,
  }
}
